/*
 * The @Property_People feed in the home page search panel.
 *
 * It first tries X's own embed snippet:
 *
 *   <a class="twitter-timeline" href="https://x.com/Property_People">…</a>
 *   <script async src="https://platform.x.com/widgets.js" charset="utf-8">
 *
 * (Not react-twitter-embed, which the contact page uses: that package still
 * loads platform.twitter.com.)
 *
 * X IS CURRENTLY SERVING NO POSTS FOR THIS ACCOUNT. Asked for the timeline
 * directly, syndication.twitter.com returns a valid response with an empty
 * entry list for Property_People while returning 20 for nasa and BBCNews in
 * the same run - so widgets.js has nothing to render and leaves the anchor
 * as a bare link. The account is public and active (2,681 posts), so this is
 * X's embed service, not the markup; it has been unreliable since 2023.
 *
 * So: if no timeline has rendered a moment after the script loads, the
 * follow card below replaces it. If X starts serving the feed again, the
 * embed wins and the card is never shown - nothing to change here.
 */

import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const SRC = "https://platform.x.com/widgets.js";

// How long to give X to swap the anchor for a timeline before giving up.
const RENDER_GRACE_MS = 4000;

let loadPromise = null;

function loadWidgets() {
    if (window.twttr && window.twttr.widgets) return Promise.resolve(window.twttr);
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${SRC}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve(window.twttr));
            existing.addEventListener("error", reject);
            return;
        }
        const script = document.createElement("script");
        script.src = SRC;
        script.async = true;
        script.charset = "utf-8";
        script.onload = () => resolve(window.twttr);
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return loadPromise;
}

/*
 * A rendered timeline is a VISIBLE iframe. When X has no posts to show it
 * still leaves an iframe behind - "position:absolute; visibility:hidden;
 * width:0" but, confusingly, a 900px height - so height alone is not enough.
 */
function hasTimeline(el) {
    const frame = el && el.querySelector("iframe");
    if (!frame) return false;
    const box = frame.getBoundingClientRect();
    return box.width > 0 && box.height > 0 && getComputedStyle(frame).visibility !== "hidden";
}

const Card = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    padding: 1.5rem;
    border: 1px solid #e3e9f2;
    border-radius: 10px;
    background: #f4f7fb;
    text-align: center;
`;

const Logo = styled.svg`
    width: 1.75rem;
    height: 1.75rem;
    margin: 0 auto 0.75rem;
    color: ${(props) => props.theme.darkblue};
`;

const Title = styled.div`
    font-size: 1rem;
    font-weight: 500;
    color: ${(props) => props.theme.darkblue};
`;

const Handle = styled.div`
    font-size: 0.85rem;
    margin-top: 0.15rem;
    color: #5a6472;
`;

const Blurb = styled.p`
    font-size: 0.85rem;
    line-height: 1.5;
    margin: 0.75rem 0 1.1rem;
    color: #5a6472;
`;

const FollowLink = styled.a`
    align-self: center;
    background: ${(props) => props.theme.darkblue};
    color: white;
    font-size: 0.85rem;
    font-weight: 500;
    padding: 0.55rem 1.4rem;
    border-radius: 8px;
    transition: 0.3s;
    &:hover {
        filter: brightness(1.4);
    }
`;

/* Same mark as the footer's social row (see Footer.js). */
function XMark(props) {
    return (
        <Logo viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
            <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
        </Logo>
    );
}

export default function XTimeline({ screenName, height = 300 }) {
    const ref = useRef(null);
    const [showCard, setShowCard] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let timer;

        loadWidgets()
            .then((twttr) => {
                if (cancelled || !ref.current || !twttr || !twttr.widgets) return;
                twttr.widgets.load(ref.current);
                timer = setTimeout(() => {
                    if (!cancelled && !hasTimeline(ref.current)) setShowCard(true);
                }, RENDER_GRACE_MS);
            })
            .catch(() => {
                if (!cancelled) setShowCard(true);
            });

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, []);

    const profileUrl = `https://x.com/${screenName}`;

    if (showCard) {
        return (
            <Card>
                <XMark />
                <Title>Follow us on X</Title>
                <Handle>@{screenName}</Handle>
                <Blurb>New instructions, market updates and South West London property news.</Blurb>
                <FollowLink href={profileUrl} target="_blank" rel="noreferrer">
                    Follow @{screenName}
                </FollowLink>
            </Card>
        );
    }

    return (
        <div ref={ref}>
            <a
                className="twitter-timeline"
                data-height={height}
                data-dnt="true"
                href={`${profileUrl}?ref_src=twsrc%5Etfw`}
            >
                Posts by {screenName}
            </a>
        </div>
    );
}
